import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc,
  Timestamp,
  orderBy,
  limit,
  DocumentData,
  QuerySnapshot,
  onSnapshot,
  doc,
  getDoc
} from 'firebase/firestore';
import { db, functions } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { differenceInMinutes, addMinutes, startOfDay, endOfDay } from 'date-fns';

interface ConsultationData {
  id: string;
  clientName: string;
  consultationType: 'in-person' | 'virtual';
  date: Date;
  time: string;
  location?: string;
  meetLink?: string;
  notes?: string;
  consultantId: string;
  /** 'calendarEvent' = call-centre / diary booking (in-app reminders only). */
  source?: 'consultation' | 'calendarEvent';
}

interface ReminderPreferences {
  inApp: {
    fifteenMin: boolean;
    oneHour: boolean;
  };
  email: {
    twentyFourHour: boolean;
    oneHour: boolean;
  };
}

interface SentReminder {
  consultationId: string;
  type: 'inApp' | 'email';
  timing: '15min' | '1hour' | '24hour';
  sentAt: Timestamp;
}

class ConsultationReminderService {
  private checkInterval: NodeJS.Timeout | null = null;
  private unsubscribe: (() => void) | null = null;
  private sentReminders: Set<string> = new Set();

  constructor() {
    this.loadSentReminders();
  }

  private async loadSentReminders() {
    const stored = localStorage.getItem('sentConsultationReminders');
    if (stored) {
      const reminders: Array<{ sentAt: string; consultationId: string; type: string; timing: string }> = JSON.parse(stored);
      const today = startOfDay(new Date()).getTime();
      
      const todaysReminders = reminders.filter((r) => {
        const reminderDate = new Date(r.sentAt).getTime();
        return reminderDate >= today;
      });
      
      todaysReminders.forEach((r) => {
        this.sentReminders.add(this.getReminderKey(r.consultationId, r.type, r.timing));
      });
      
      localStorage.setItem('sentConsultationReminders', JSON.stringify(todaysReminders));
    }
  }

  private getReminderKey(consultationId: string, type: string, timing: string): string {
    return `${consultationId}_${type}_${timing}`;
  }

  private async markReminderSent(consultationId: string, type: 'inApp' | 'email', timing: '15min' | '1hour' | '24hour') {
    const key = this.getReminderKey(consultationId, type, timing);
    this.sentReminders.add(key);
    
    const stored = localStorage.getItem('sentConsultationReminders') || '[]';
    const reminders = JSON.parse(stored);
    reminders.push({
      consultationId,
      type,
      timing,
      sentAt: new Date().toISOString()
    });
    localStorage.setItem('sentConsultationReminders', JSON.stringify(reminders));
  }

  private wasReminderSent(consultationId: string, type: string, timing: string): boolean {
    return this.sentReminders.has(this.getReminderKey(consultationId, type, timing));
  }

  async getUserPreferences(userId: string): Promise<ReminderPreferences> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userData = userDoc.data();
      
      return userData?.reminderPreferences || {
        inApp: { fifteenMin: true, oneHour: true },
        email: { twentyFourHour: true, oneHour: true }
      };
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      return {
        inApp: { fifteenMin: true, oneHour: true },
        email: { twentyFourHour: true, oneHour: true }
      };
    }
  }

  async getUpcomingConsultations(userId: string): Promise<ConsultationData[]> {
    try {
      const now = new Date();
      const endOfToday = endOfDay(now);
      
      const q = query(
        collection(db, 'consultations'),
        where('consultantId', '==', userId),
        where('status', '==', 'scheduled'),
        where('scheduledDate', '>=', Timestamp.fromDate(now)),
        where('scheduledDate', '<=', Timestamp.fromDate(endOfToday)),
        orderBy('scheduledDate', 'asc')
      );

      const snapshot = await getDocs(q);

      // Call-centre / linked diary bookings live in calendarEvents (Jaco
      // 2026-10-22): without them the dashboard's "today" tile, agenda and
      // in-app reminders missed most of the consultant's meetings.
      const { isClientMeetingEvent } = await import('./consultantBookings');
      const eventsSnap = await getDocs(
        query(
          collection(db, 'calendarEvents'),
          where('userId', '==', userId),
          where('startTime', '>=', Timestamp.fromDate(now)),
          where('startTime', '<=', Timestamp.fromDate(endOfToday)),
          orderBy('startTime', 'asc')
        )
      ).catch((err) => {
        console.error('Error fetching call-centre bookings for today:', err);
        return null;
      });
      // Google copies of PropCloud bookings carry the same googleEventId.
      const consultationGoogleIds = new Set(
        [...snapshot.docs.map((d) => d.data()), ...(eventsSnap?.docs || []).map((d) => d.data()).filter((x) => x.isFromGoogle !== true)]
          .map((x) => x.googleEventId)
          .filter(Boolean),
      );
      const eventRows: ConsultationData[] = (eventsSnap?.docs || [])
        .filter((d) => {
          const x = d.data();
          return x.status !== 'cancelled' && isClientMeetingEvent(x) && !(x.googleEventId && consultationGoogleIds.has(x.googleEventId));
        })
        .map((d) => {
          const x = d.data();
          const dateTime = x.startTime.toDate();
          return {
            id: d.id,
            clientName: x.contactName || String(x.summary || '').split(/\s[—-]\s|\n/)[0] || 'Client',
            consultationType: x.meetingType === 'google_meet' ? 'virtual' : 'in-person',
            date: dateTime,
            time: dateTime.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }),
            location: x.location,
            meetLink: x.meetLink,
            notes: x.description,
            consultantId: x.userId,
            source: 'calendarEvent',
          } as ConsultationData;
        });

      const consultationRows = snapshot.docs.map((doc): ConsultationData => {
        const data = doc.data();
        const dateTime = data.scheduledDate.toDate();
        
        return {
          id: doc.id,
          clientName: data.contactName || 'Unknown Client',
          consultationType: data.meetingType === 'google_meet' ? 'virtual' : 'in-person',
          date: dateTime,
          time: dateTime.toLocaleTimeString('en-ZA', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }),
          location: data.location,
          meetLink: data.googleMeetLink,
          notes: data.notes,
          consultantId: data.consultantId
        };
      });
      return [...consultationRows, ...eventRows].sort((a, b) => a.date.getTime() - b.date.getTime());
    } catch (error) {
      console.error('Error fetching upcoming consultations:', error);
      return [];
    }
  }

  async checkForReminders(
    userId: string, 
    onInAppReminder?: (consultation: ConsultationData) => void
  ): Promise<void> {
    try {
      const [consultations, preferences] = await Promise.all([
        this.getUpcomingConsultations(userId),
        this.getUserPreferences(userId)
      ]);

      const now = new Date();

      for (const consultation of consultations) {
        const minutesUntil = differenceInMinutes(consultation.date, now);

        // 15-minute in-app reminder
        if (preferences.inApp.fifteenMin && 
            minutesUntil >= 14 && minutesUntil <= 16 &&
            !this.wasReminderSent(consultation.id, 'inApp', '15min')) {
          
          if (onInAppReminder) {
            onInAppReminder(consultation);
          }
          await this.markReminderSent(consultation.id, 'inApp', '15min');
        }

        // 1-hour in-app reminder
        if (preferences.inApp.oneHour && 
            minutesUntil >= 59 && minutesUntil <= 61 &&
            !this.wasReminderSent(consultation.id, 'inApp', '1hour')) {
          
          if (onInAppReminder) {
            onInAppReminder(consultation);
          }
          await this.markReminderSent(consultation.id, 'inApp', '1hour');
        }

        // 1-hour email reminder
        if (preferences.email.oneHour && consultation.source !== 'calendarEvent' &&
            minutesUntil >= 59 && minutesUntil <= 61 &&
            !this.wasReminderSent(consultation.id, 'email', '1hour')) {
          
          await this.sendEmailReminder(consultation, '1hour');
          await this.markReminderSent(consultation.id, 'email', '1hour');
        }
      }
    } catch (error) {
      console.error('Error checking for reminders:', error);
    }
  }

  private async sendEmailReminder(consultation: ConsultationData, timing: '1hour' | '24hour') {
    try {
      // Use Firebase Function instead of Netlify function
      const sendConsultationReminder = httpsCallable(functions, 'sendConsultationReminder');
      await sendConsultationReminder({
        consultation,
        timing
      });
    } catch (error) {
      console.error('Error sending email reminder:', error);
    }
  }

  startReminderChecking(
    userId: string,
    onInAppReminder?: (consultation: ConsultationData) => void
  ): () => void {
    // Initial check
    this.checkForReminders(userId, onInAppReminder);

    // Check every minute for reminders
    this.checkInterval = setInterval(() => {
      this.checkForReminders(userId, onInAppReminder);
    }, 60000); // Check every minute

    // Also listen to real-time updates for new consultations
    const now = new Date();
    const endOfToday = endOfDay(now);
    
    const q = query(
      collection(db, 'consultations'),
      where('consultantId', '==', userId),
      where('status', '==', 'scheduled'),
      where('scheduledDate', '>=', Timestamp.fromDate(now)),
      where('scheduledDate', '<=', Timestamp.fromDate(endOfToday))
    );

    this.unsubscribe = onSnapshot(q, () => {
      // When consultations change, recheck for reminders
      this.checkForReminders(userId, onInAppReminder);
    });

    // Return cleanup function
    return () => this.stopReminderChecking();
  }

  stopReminderChecking() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  // Check for 24-hour reminders (should be called once daily or via scheduled function)
  async check24HourReminders(userId: string): Promise<void> {
    try {
      const preferences = await this.getUserPreferences(userId);
      
      if (!preferences.email.twentyFourHour) {
        return;
      }

      const tomorrow = addMinutes(new Date(), 24 * 60);
      const tomorrowStart = startOfDay(tomorrow);
      const tomorrowEnd = endOfDay(tomorrow);
      
      const q = query(
        collection(db, 'consultations'),
        where('consultantId', '==', userId),
        where('status', '==', 'scheduled'),
        where('scheduledDate', '>=', Timestamp.fromDate(tomorrowStart)),
        where('scheduledDate', '<=', Timestamp.fromDate(tomorrowEnd))
      );

      const snapshot = await getDocs(q);
      
      for (const doc of snapshot.docs) {
        const data = doc.data();
        const consultation: ConsultationData = {
          id: doc.id,
          clientName: data.contactName || 'Unknown Client',
          consultationType: data.meetingType === 'google_meet' ? 'virtual' : 'in-person',
          date: data.scheduledDate.toDate(),
          time: data.scheduledDate.toDate().toLocaleTimeString('en-ZA', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }),
          location: data.location,
          meetLink: data.meetLink,
          notes: data.notes,
          consultantId: data.consultantId
        };

        if (!this.wasReminderSent(consultation.id, 'email', '24hour')) {
          await this.sendEmailReminder(consultation, '24hour');
          await this.markReminderSent(consultation.id, 'email', '24hour');
        }
      }
    } catch (error) {
      console.error('Error checking 24-hour reminders:', error);
    }
  }
}

// Create singleton instance
export const reminderService = new ConsultationReminderService();